import { getOauthClientId } from '@local/app-config';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import { LOGIN_SUCCESS_ROUTE } from '../../../constants';
import {
    AUTH_CODE_KEY,
    getCombinedToken as getAccessToken,
    setCombinedToken as setAccessToken,
} from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { getPkceVerifier } from '../../../store/sessionStorageHelpers/pkceFlowHelpers';
import type { AccessAndIDTokenResponse as AccessTokenResponse } from '../../types';
import { IdentityApiClient } from '../IdentityApiClient';

export function accessTokenBody(
    code: string,
    includeClientSecret: boolean,
    forceOauthPrefix: boolean,
) {
    const clientId = getOauthClientId(forceOauthPrefix);
    const redirectUrl = `${window.location.origin}${LOGIN_SUCCESS_ROUTE}` || '';

    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('code', code);
    urlEncodedBody.append('grant_type', 'authorization_code');
    urlEncodedBody.append('redirect_uri', redirectUrl);
    urlEncodedBody.append('client_id', clientId);
    if (includeClientSecret) {
        // todo remove in CENPLAT-10941
        urlEncodedBody.append('client_secret', clientId);
    }
    const verifier = getPkceVerifier();
    urlEncodedBody.append('code_verifier', verifier);

    return urlEncodedBody;
}

export function refreshTokenBody(
    refreshToken: string,
    forceOauthPrefix: boolean,
    includeClientSecret: boolean,
) {
    const clientId = getOauthClientId(forceOauthPrefix);

    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('grant_type', 'refresh_token');
    urlEncodedBody.append('refresh_token', refreshToken);
    urlEncodedBody.append('client_id', clientId);
    if (includeClientSecret) {
        // todo remove in CENPLAT-10941
        urlEncodedBody.append('client_secret', clientId);
    }

    return urlEncodedBody;
}

interface AccessTokenParams {
    authCode: string;
    includeClientSecret: boolean;
    forceOauthPrefix: boolean;
}
interface RefreshAccessTokenParams extends Omit<AccessTokenParams, 'authCode'> {
    refreshToken: string;
}
const tokenMutex = new Mutex();

export const AccessTokenApi = IdentityApiClient.injectEndpoints({
    endpoints: (build) => ({
        retrieveAccessToken: build.query<AccessTokenResponse, AccessTokenParams>({
            async queryFn(
                { authCode, includeClientSecret, forceOauthPrefix },
                _api,
                _extraOptions,
                baseQuery,
            ) {
                const accessTokenResponse = await baseQuery({
                    url: 'oauth2/access',
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: accessTokenBody(authCode, includeClientSecret, forceOauthPrefix),
                });
                if (accessTokenResponse.error) {
                    return { error: accessTokenResponse.error };
                }
                setAccessToken(accessTokenResponse.data as AccessTokenResponse);
                sessionStorage.setItem(AUTH_CODE_KEY, authCode);
                return { data: accessTokenResponse.data as AccessTokenResponse };
            },
        }),
        refreshAccessToken: build.query<AccessTokenResponse, RefreshAccessTokenParams>({
            async queryFn(
                { forceOauthPrefix, includeClientSecret, refreshToken },
                _api,
                _extraOptions,
                baseQuery,
            ) {
                const accessTokenResponse = await baseQuery({
                    url: 'oauth2/access',
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: refreshTokenBody(refreshToken, forceOauthPrefix, includeClientSecret),
                });
                if (accessTokenResponse.error) {
                    return { error: accessTokenResponse.error };
                }
                setAccessToken(accessTokenResponse.data as AccessTokenResponse);
                return { data: accessTokenResponse.data as AccessTokenResponse };
            },
        }),
    }),
});

export interface UrlConfigType extends Omit<AccessTokenParams, 'authCode'> {
    url?: string;
}
export type UrlConfigCallback = () => UrlConfigType | null;

export function createAccessTokenBaseQuery(
    urlConfig: UrlConfigCallback,
    transformedFilterParams?: (args: FetchArgs) => FetchArgs,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
    return async (args, WebApi, extraOptions) => {
        let currentArgs;
        if (transformedFilterParams) {
            currentArgs = transformedFilterParams(args as FetchArgs);
        } else {
            currentArgs = args;
        }

        const urlConfiguration = urlConfig();

        if (urlConfiguration === null) {
            return { error: { status: 500, data: 'Not enough information to generate url' } };
        }

        const { url: baseUrl, forceOauthPrefix, includeClientSecret } = urlConfiguration;
        const rawBaseQuery = fetchBaseQuery({
            baseUrl,
            prepareHeaders: (headers) => {
                const token = getAccessToken();
                if (token) {
                    headers.set('authorization', `Bearer ${token?.access_token}`);
                }
                return headers;
            },
        });
        // We have a mutex to prevent fetching a token multiple times if a number of calls happen at once.
        await tokenMutex.waitForUnlock();
        let result = await rawBaseQuery(currentArgs, WebApi, extraOptions);
        // If the result errors out with a 401, we probably have an expired access token.
        const originalToken = getAccessToken();
        if (result.error && result.error.status === 401 && originalToken) {
            const refreshToken = originalToken?.refresh_token ?? false;
            if (!refreshToken) {
                // Return the error, we don't have enough information to get a new one.
                return result;
            }
            if (!tokenMutex.isLocked()) {
                // Actually fetch the new access token, forcing a refetch that
                // subscribes to result to make sure the request is completed
                const refreshResult = await WebApi.dispatch(
                    AccessTokenApi.endpoints.refreshAccessToken.initiate(
                        {
                            forceOauthPrefix,
                            includeClientSecret,
                            refreshToken,
                        },
                        { subscribe: true, forceRefetch: true },
                    ),
                );

                // If we actually got a new token, try the original query again.
                const newToken = getAccessToken();
                if (
                    refreshResult?.isSuccess &&
                    originalToken.access_token !== newToken?.access_token
                ) {
                    // retry the initial query
                    result = await rawBaseQuery(currentArgs, WebApi, extraOptions);
                }
            } else {
                // There is another query fetching a token. Wait for that, and try query again assuming we have a new
                // token now.
                await tokenMutex.waitForUnlock();
                result = await rawBaseQuery(currentArgs, WebApi, extraOptions);
            }
        }
        return result;
    };
}

export const { useRetrieveAccessTokenQuery, useLazyRefreshAccessTokenQuery } = AccessTokenApi;
